import { getLinkableContactObjectName, type IntegrationServiceId } from '../../../../common/integrations'
import { withCompleteContactLinksOnly } from '../../../../common/contactLinks'
import { type IntegrationSettings } from './schema'
import {
  AccountIntegrationConfigMappingType,
  type CRMAccountIntegrationConfig,
  type CustomObjectMapping,
  type DetailedCRMAccountIntegrationMapping,
  getMappingTypeLabel,
  isLockableMappingType,
  MappingManagedBy,
} from '../../../../models/Account'

export const isUiManaged = (mapping: { managedBy?: MappingManagedBy | null }): boolean =>
  mapping.managedBy === undefined || mapping.managedBy === null || mapping.managedBy === MappingManagedBy.UI

type MappingEntry = DetailedCRMAccountIntegrationMapping | CustomObjectMapping | null | undefined

const mappingEntries = (
  mappingType: AccountIntegrationConfigMappingType,
  mapping: MappingEntry | MappingEntry[] | Record<string, MappingEntry> | undefined,
): MappingEntry[] => {
  if (!mapping) {
    return []
  }
  if (Array.isArray(mapping)) {
    return mapping
  }
  if (mappingType === AccountIntegrationConfigMappingType.BUSINESS_EVENTS) {
    return Object.values(mapping as Record<string, MappingEntry>)
  }
  return [mapping as MappingEntry]
}

export const isMappingTypeLocked = (
  integrationConfig: CRMAccountIntegrationConfig | null | undefined,
  mappingType: AccountIntegrationConfigMappingType,
): boolean => {
  if (!integrationConfig) {
    return false
  }
  if (integrationConfig.isCustom) {
    return true
  }
  return mappingEntries(mappingType, integrationConfig.mappings?.[mappingType]).some((entry) => !!entry && !isUiManaged(entry))
}

export const isSameContactObject = (serviceId: IntegrationServiceId, left: string | null | undefined, right: string | null | undefined): boolean => {
  const linkableLeft = getLinkableContactObjectName(serviceId, left ?? null)
  return !!linkableLeft && linkableLeft === getLinkableContactObjectName(serviceId, right ?? null)
}

export const getContactObjectLockedBy = (integrationConfig: CRMAccountIntegrationConfig | null | undefined): AccountIntegrationConfigMappingType[] =>
  Object.values(AccountIntegrationConfigMappingType).filter(
    (mappingType) =>
      isLockableMappingType(mappingType) && mappingType !== AccountIntegrationConfigMappingType.CONTACT && isMappingTypeLocked(integrationConfig, mappingType),
  )

export const formatMappingTypeLabels = (mappingTypes: AccountIntegrationConfigMappingType[]): string => {
  return new Intl.ListFormat('en', { type: 'conjunction' }).format(mappingTypes.map(getMappingTypeLabel))
}

type FormMappings = IntegrationSettings['mappings']

export const withCompleteEditableContactLinks = (mappings: FormMappings, integrationConfig: CRMAccountIntegrationConfig | null | undefined): FormMappings => {
  const isEditable = (mappingType: AccountIntegrationConfigMappingType) => !isMappingTypeLocked(integrationConfig, mappingType)
  return {
    ...mappings,
    ...(mappings?.customObjects?.length && isEditable(AccountIntegrationConfigMappingType.CUSTOM_OBJECTS)
      ? { customObjects: mappings.customObjects.map(withCompleteContactLinksOnly) }
      : {}),
    ...(mappings?.calendar && isEditable(AccountIntegrationConfigMappingType.CALENDAR) ? { calendar: withCompleteContactLinksOnly(mappings.calendar) } : {}),
  }
}
